/**
 * Resource Slice for Wadakaroo
 *
 * Manages state for all workshop resources:
 * - Loan Cars
 * - Equipment
 * - Tools
 * - Spaces
 * - Consultants
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getLoanCarsApi,
    getLoanCarApi,
    createLoanCarApi,
    updateLoanCarApi,
    deleteLoanCarApi,
    getLoanCarAllocationsApi,
    getLoanCarIncidentHistoryApi,
    getLoanCarRegoStatusApi,
    getEquipmentListApi,
    getEquipmentApi,
    createEquipmentApi,
    updateEquipmentApi,
    deleteEquipmentApi,
    getToolsApi,
    getToolApi,
    createToolApi,
    updateToolApi,
    deleteToolApi,
    getSpacesApi,
    getSpaceApi,
    createSpaceApi,
    updateSpaceApi,
    deleteSpaceApi,
    getConsultantsApi,
    getConsultantApi,
    createConsultantApi,
    updateConsultantApi,
    deleteConsultantApi,
    updateResourceStatusApi,
    getResourceSummaryApi,
} from '../../services/resourceApi';

// ============================================
// Async Thunks - Loan Cars
// ============================================

export const fetchLoanCars = createAsyncThunk(
    'resources/fetchLoanCars',
    async (_, { rejectWithValue }) => {
        try {
            return await getLoanCarsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch loan cars');
        }
    }
);

export const fetchLoanCar = createAsyncThunk(
    'resources/fetchLoanCar',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getLoanCarApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch loan car');
        }
    }
);

export const createLoanCar = createAsyncThunk(
    'resources/createLoanCar',
    async (data, { rejectWithValue }) => {
        try {
            return await createLoanCarApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create loan car');
        }
    }
);

export const updateLoanCar = createAsyncThunk(
    'resources/updateLoanCar',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateLoanCarApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update loan car');
        }
    }
);

export const deleteLoanCar = createAsyncThunk(
    'resources/deleteLoanCar',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteLoanCarApi(uuid);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete loan car');
        }
    }
);

/**
 * Fetches all allocations for a specific loan car to power the Gantt schedule view.
 * Dispatched when user clicks "Booking Schedule" on a loan car card.
 */
export const fetchLoanCarAllocations = createAsyncThunk(
    'resources/fetchLoanCarAllocations',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getLoanCarAllocationsApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking schedule');
        }
    }
);

/**
 * Fetches paginated incident history for a loan car rego plate, newest-first.
 * Dispatched when user opens the History modal or pages through it.
 */
export const fetchLoanCarIncidentHistory = createAsyncThunk(
    'resources/fetchLoanCarIncidentHistory',
    async ({ rego, page = 0 }, { rejectWithValue }) => {
        try {
            return await getLoanCarIncidentHistoryApi(rego, page);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch loan car history'
            );
        }
    }
);

/**
 * Batch-fetches the current incident status for all loan car regos.
 * Dispatched after fetchLoanCars resolves. Builds a { [rego]: boolean } map
 * where true = car has an active OUT with no paired IN.
 */
export const fetchLoanCarRegoStatuses = createAsyncThunk(
    'resources/fetchLoanCarRegoStatuses',
    async (regos, { rejectWithValue }) => {
        try {
            return await getLoanCarRegoStatusApi(regos);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch rego statuses');
        }
    }
);

// ============================================
// Async Thunks - Equipment
// ============================================

export const fetchEquipment = createAsyncThunk(
    'resources/fetchEquipment',
    async (_, { rejectWithValue }) => {
        try {
            return await getEquipmentListApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch equipment');
        }
    }
);

export const fetchEquipmentItem = createAsyncThunk(
    'resources/fetchEquipmentItem',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getEquipmentApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch equipment');
        }
    }
);

export const createEquipment = createAsyncThunk(
    'resources/createEquipment',
    async (data, { rejectWithValue }) => {
        try {
            return await createEquipmentApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create equipment');
        }
    }
);

export const updateEquipment = createAsyncThunk(
    'resources/updateEquipment',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateEquipmentApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update equipment');
        }
    }
);

export const deleteEquipment = createAsyncThunk(
    'resources/deleteEquipment',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteEquipmentApi(uuid);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete equipment');
        }
    }
);

// ============================================
// Async Thunks - Tools
// ============================================

export const fetchTools = createAsyncThunk(
    'resources/fetchTools',
    async (_, { rejectWithValue }) => {
        try {
            return await getToolsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch tools');
        }
    }
);

export const fetchTool = createAsyncThunk(
    'resources/fetchTool',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getToolApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch tool');
        }
    }
);

export const createTool = createAsyncThunk(
    'resources/createTool',
    async (data, { rejectWithValue }) => {
        try {
            return await createToolApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create tool');
        }
    }
);

export const updateTool = createAsyncThunk(
    'resources/updateTool',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateToolApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update tool');
        }
    }
);

export const deleteTool = createAsyncThunk(
    'resources/deleteTool',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteToolApi(uuid);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete tool');
        }
    }
);

// ============================================
// Async Thunks - Spaces
// ============================================

export const fetchSpaces = createAsyncThunk(
    'resources/fetchSpaces',
    async (_, { rejectWithValue }) => {
        try {
            return await getSpacesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch spaces');
        }
    }
);

export const fetchSpace = createAsyncThunk(
    'resources/fetchSpace',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getSpaceApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch space');
        }
    }
);

export const createSpace = createAsyncThunk(
    'resources/createSpace',
    async (data, { rejectWithValue }) => {
        try {
            return await createSpaceApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create space');
        }
    }
);

export const updateSpace = createAsyncThunk(
    'resources/updateSpace',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateSpaceApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update space');
        }
    }
);

export const deleteSpace = createAsyncThunk(
    'resources/deleteSpace',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteSpaceApi(uuid);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete space');
        }
    }
);

// ============================================
// Async Thunks - Consultants
// ============================================

export const fetchConsultants = createAsyncThunk(
    'resources/fetchConsultants',
    async (_, { rejectWithValue }) => {
        try {
            return await getConsultantsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch consultants');
        }
    }
);

export const fetchConsultant = createAsyncThunk(
    'resources/fetchConsultant',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getConsultantApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch consultant');
        }
    }
);

export const createConsultant = createAsyncThunk(
    'resources/createConsultant',
    async (data, { rejectWithValue }) => {
        try {
            return await createConsultantApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create consultant');
        }
    }
);

export const updateConsultant = createAsyncThunk(
    'resources/updateConsultant',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateConsultantApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update consultant');
        }
    }
);

export const deleteConsultant = createAsyncThunk(
    'resources/deleteConsultant',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteConsultantApi(uuid);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete consultant');
        }
    }
);

// ============================================
// Async Thunks - Common
// ============================================

export const updateResourceStatus = createAsyncThunk(
    'resources/updateStatus',
    async ({ uuid, status }, { rejectWithValue }) => {
        try {
            return await updateResourceStatusApi(uuid, status);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update status');
        }
    }
);

export const fetchResourceSummary = createAsyncThunk(
    'resources/fetchSummary',
    async (_, { rejectWithValue }) => {
        try {
            return await getResourceSummaryApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch summary');
        }
    }
);

// ============================================
// Initial State
// ============================================

const initialState = {
    // Loan Cars
    loanCars: [],
    loanCarsLoading: false,
    loanCarsError: null,
    selectedLoanCar: null,

    // Equipment
    equipment: [],
    equipmentLoading: false,
    equipmentError: null,
    selectedEquipment: null,

    // Tools
    tools: [],
    toolsLoading: false,
    toolsError: null,
    selectedTool: null,

    // Spaces
    spaces: [],
    spacesLoading: false,
    spacesError: null,
    selectedSpace: null,

    // Consultants
    consultants: [],
    consultantsLoading: false,
    consultantsError: null,
    selectedConsultant: null,

    // Summary
    summary: null,
    summaryLoading: false,

    // UI State
    activeTab: 'loan-cars',
    showCreateModal: false,
    showEditModal: false,
    showDeleteConfirm: false,
    itemToDelete: null,
    operationLoading: false,
    operationError: null,
    operationSuccess: null,

    // Loan Car Schedule (Gantt) Modal
    showScheduleModal: false,
    scheduleCarUuid: null,
    scheduleCarDetails: null,
    allocations: [],
    allocationsLoading: false,
    allocationsError: null,

    // Loan Car History Modal
    showHistoryModal: false,
    historyCarDetails: null,
    historyIncidents: [],
    historyPage: 0,
    historyTotalPages: 0,
    historyTotalElements: 0,
    historyLoading: false,
    historyError: null,

    // Loan Car Rego Incident Status { [rego]: boolean }
    // true = active OUT with no paired IN (car currently with customer)
    loanCarRegoStatuses: {},
};

// ============================================
// Slice
// ============================================

const resourceSlice = createSlice({
    name: 'resources',
    initialState,
    reducers: {
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        openCreateModal: (state) => {
            state.showCreateModal = true;
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeCreateModal: (state) => {
            state.showCreateModal = false;
        },
        openEditModal: (state, action) => {
            state.showEditModal = true;
            state.operationError = null;
            state.operationSuccess = null;
            const { type, item } = action.payload;
            switch (type) {
                case 'loan-cars': state.selectedLoanCar = item; break;
                case 'equipment': state.selectedEquipment = item; break;
                case 'tools': state.selectedTool = item; break;
                case 'spaces': state.selectedSpace = item; break;
                case 'consultants': state.selectedConsultant = item; break;
            }
        },
        closeEditModal: (state) => {
            state.showEditModal = false;
            state.selectedLoanCar = null;
            state.selectedEquipment = null;
            state.selectedTool = null;
            state.selectedSpace = null;
            state.selectedConsultant = null;
        },
        openDeleteConfirm: (state, action) => {
            state.showDeleteConfirm = true;
            state.itemToDelete = action.payload;
        },
        closeDeleteConfirm: (state) => {
            state.showDeleteConfirm = false;
            state.itemToDelete = null;
        },
        clearOperationStatus: (state) => {
            state.operationError = null;
            state.operationSuccess = null;
        },

        // ---- Schedule modal actions ----
        openScheduleModal: (state, action) => {
            state.showScheduleModal = true;
            state.scheduleCarUuid = action.payload.uuid;
            state.scheduleCarDetails = action.payload;
            state.allocations = [];
            state.allocationsError = null;
        },
        closeScheduleModal: (state) => {
            state.showScheduleModal = false;
            state.scheduleCarUuid = null;
            state.scheduleCarDetails = null;
            state.allocations = [];
            state.allocationsError = null;
        },

        // ---- History modal actions ----
        openHistoryModal: (state, action) => {
            state.showHistoryModal     = true;
            state.historyCarDetails    = action.payload;
            state.historyIncidents     = [];
            state.historyPage          = 0;
            state.historyTotalPages    = 0;
            state.historyTotalElements = 0;
            state.historyError         = null;
        },
        closeHistoryModal: (state) => {
            state.showHistoryModal     = false;
            state.historyCarDetails    = null;
            state.historyIncidents     = [];
            state.historyPage          = 0;
            state.historyTotalPages    = 0;
            state.historyTotalElements = 0;
            state.historyError         = null;
        },
    },
    extraReducers: (builder) => {
        // ==================== Loan Cars ====================
        builder
            .addCase(fetchLoanCars.pending, (state) => {
                state.loanCarsLoading = true;
                state.loanCarsError = null;
            })
            .addCase(fetchLoanCars.fulfilled, (state, action) => {
                state.loanCarsLoading = false;
                state.loanCars = action.payload;
            })
            .addCase(fetchLoanCars.rejected, (state, action) => {
                state.loanCarsLoading = false;
                state.loanCarsError = action.payload;
            })
            .addCase(createLoanCar.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createLoanCar.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.loanCars.push(action.payload);
                state.showCreateModal = false;
                state.operationSuccess = 'Loan car created successfully';
            })
            .addCase(createLoanCar.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })
            .addCase(updateLoanCar.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateLoanCar.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.loanCars.findIndex(c => c.uuid === action.payload.uuid);
                if (index !== -1) state.loanCars[index] = action.payload;
                state.showEditModal = false;
                state.selectedLoanCar = null;
                state.operationSuccess = 'Loan car updated successfully';
            })
            .addCase(updateLoanCar.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })
            .addCase(deleteLoanCar.fulfilled, (state, action) => {
                state.loanCars = state.loanCars.filter(c => c.uuid !== action.payload);
                state.showDeleteConfirm = false;
                state.itemToDelete = null;
                state.operationSuccess = 'Loan car deleted successfully';
            })

            // ==================== Loan Car Allocations (Schedule) ====================
            .addCase(fetchLoanCarAllocations.pending, (state) => {
                state.allocationsLoading = true;
                state.allocationsError = null;
            })
            .addCase(fetchLoanCarAllocations.fulfilled, (state, action) => {
                state.allocationsLoading = false;
                state.allocations = action.payload;
            })
            .addCase(fetchLoanCarAllocations.rejected, (state, action) => {
                state.allocationsLoading = false;
                state.allocationsError = action.payload;
            })

            // ==================== Loan Car Incident History ====================
            .addCase(fetchLoanCarIncidentHistory.pending, (state) => {
                state.historyLoading = true;
                state.historyError   = null;
            })
            .addCase(fetchLoanCarIncidentHistory.fulfilled, (state, action) => {
                state.historyLoading        = false;
                state.historyIncidents      = action.payload.content;
                state.historyPage           = action.payload.page;
                state.historyTotalPages     = action.payload.totalPages;
                state.historyTotalElements  = action.payload.totalElements;
            })
            .addCase(fetchLoanCarIncidentHistory.rejected, (state, action) => {
                state.historyLoading = false;
                state.historyError   = action.payload;
            })

            // ==================== Loan Car Rego Statuses ====================
            .addCase(fetchLoanCarRegoStatuses.fulfilled, (state, action) => {
                // Convert [{ rego, activeIncident }] → { [rego]: boolean }
                state.loanCarRegoStatuses = Object.fromEntries(
                    action.payload.map(s => [s.rego, s.activeIncident])
                );
            })

            // ==================== Equipment ====================
            .addCase(fetchEquipment.pending, (state) => {
                state.equipmentLoading = true;
                state.equipmentError = null;
            })
            .addCase(fetchEquipment.fulfilled, (state, action) => {
                state.equipmentLoading = false;
                state.equipment = action.payload;
            })
            .addCase(fetchEquipment.rejected, (state, action) => {
                state.equipmentLoading = false;
                state.equipmentError = action.payload;
            })
            .addCase(createEquipment.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createEquipment.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.equipment.push(action.payload);
                state.showCreateModal = false;
                state.operationSuccess = 'Equipment created successfully';
            })
            .addCase(createEquipment.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })
            .addCase(updateEquipment.fulfilled, (state, action) => {
                const index = state.equipment.findIndex(e => e.uuid === action.payload.uuid);
                if (index !== -1) state.equipment[index] = action.payload;
                state.showEditModal = false;
                state.selectedEquipment = null;
                state.operationSuccess = 'Equipment updated successfully';
            })
            .addCase(deleteEquipment.fulfilled, (state, action) => {
                state.equipment = state.equipment.filter(e => e.uuid !== action.payload);
                state.showDeleteConfirm = false;
                state.itemToDelete = null;
                state.operationSuccess = 'Equipment deleted successfully';
            })

            // ==================== Tools ====================
            .addCase(fetchTools.pending, (state) => {
                state.toolsLoading = true;
                state.toolsError = null;
            })
            .addCase(fetchTools.fulfilled, (state, action) => {
                state.toolsLoading = false;
                state.tools = action.payload;
            })
            .addCase(fetchTools.rejected, (state, action) => {
                state.toolsLoading = false;
                state.toolsError = action.payload;
            })
            .addCase(createTool.fulfilled, (state, action) => {
                state.tools.push(action.payload);
                state.showCreateModal = false;
                state.operationSuccess = 'Tool created successfully';
            })
            .addCase(updateTool.fulfilled, (state, action) => {
                const index = state.tools.findIndex(t => t.uuid === action.payload.uuid);
                if (index !== -1) state.tools[index] = action.payload;
                state.showEditModal = false;
                state.selectedTool = null;
                state.operationSuccess = 'Tool updated successfully';
            })
            .addCase(deleteTool.fulfilled, (state, action) => {
                state.tools = state.tools.filter(t => t.uuid !== action.payload);
                state.showDeleteConfirm = false;
                state.itemToDelete = null;
                state.operationSuccess = 'Tool deleted successfully';
            })

            // ==================== Spaces ====================
            .addCase(fetchSpaces.pending, (state) => {
                state.spacesLoading = true;
                state.spacesError = null;
            })
            .addCase(fetchSpaces.fulfilled, (state, action) => {
                state.spacesLoading = false;
                state.spaces = action.payload;
            })
            .addCase(fetchSpaces.rejected, (state, action) => {
                state.spacesLoading = false;
                state.spacesError = action.payload;
            })
            .addCase(createSpace.fulfilled, (state, action) => {
                state.spaces.push(action.payload);
                state.showCreateModal = false;
                state.operationSuccess = 'Space created successfully';
            })
            .addCase(updateSpace.fulfilled, (state, action) => {
                const index = state.spaces.findIndex(s => s.uuid === action.payload.uuid);
                if (index !== -1) state.spaces[index] = action.payload;
                state.showEditModal = false;
                state.selectedSpace = null;
                state.operationSuccess = 'Space updated successfully';
            })
            .addCase(deleteSpace.fulfilled, (state, action) => {
                state.spaces = state.spaces.filter(s => s.uuid !== action.payload);
                state.showDeleteConfirm = false;
                state.itemToDelete = null;
                state.operationSuccess = 'Space deleted successfully';
            })

            // ==================== Consultants ====================
            .addCase(fetchConsultants.pending, (state) => {
                state.consultantsLoading = true;
                state.consultantsError = null;
            })
            .addCase(fetchConsultants.fulfilled, (state, action) => {
                state.consultantsLoading = false;
                state.consultants = action.payload;
            })
            .addCase(fetchConsultants.rejected, (state, action) => {
                state.consultantsLoading = false;
                state.consultantsError = action.payload;
            })
            .addCase(createConsultant.fulfilled, (state, action) => {
                state.consultants.push(action.payload);
                state.showCreateModal = false;
                state.operationSuccess = 'Consultant created successfully';
            })
            .addCase(updateConsultant.fulfilled, (state, action) => {
                const index = state.consultants.findIndex(c => c.uuid === action.payload.uuid);
                if (index !== -1) state.consultants[index] = action.payload;
                state.showEditModal = false;
                state.selectedConsultant = null;
                state.operationSuccess = 'Consultant updated successfully';
            })
            .addCase(deleteConsultant.fulfilled, (state, action) => {
                state.consultants = state.consultants.filter(c => c.uuid !== action.payload);
                state.showDeleteConfirm = false;
                state.itemToDelete = null;
                state.operationSuccess = 'Consultant deleted successfully';
            })

            // ==================== Summary ====================
            .addCase(fetchResourceSummary.pending, (state) => {
                state.summaryLoading = true;
            })
            .addCase(fetchResourceSummary.fulfilled, (state, action) => {
                state.summaryLoading = false;
                state.summary = action.payload;
            });
    },
});

// ============================================
// Exports
// ============================================

export const {
    setActiveTab,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteConfirm,
    closeDeleteConfirm,
    clearOperationStatus,
    openScheduleModal,
    closeScheduleModal,
    openHistoryModal,
    closeHistoryModal,
} = resourceSlice.actions;

export default resourceSlice.reducer;